/**
 * O cartão Grupo na ficha da Empresa (13.9, FR-88): o link para a ficha do Grupo, ou "Criar
 * grupo".
 *
 * **Quem lê o Grupo é o serviço**: a gestão e quem é Responsável de uma filial dele. A ficha da
 * Empresa traz só o `grupo_id`; o nome vem de `GET /grupos/{id}`, e o `404` dele (o Grupo de um
 * cliente que não é seu, ou que acabou de ser desfeito) vira "faz parte de um grupo que você não
 * acompanha" — e a ficha é relida uma vez, para o caso de o Grupo ter sumido.
 *
 * **"Criar grupo" nasce com o nome vazio** (D-9): um padrão com o nome desta Empresa o mostraria
 * aos Responsáveis das outras filiais. O botão aparece com `gerir_grupos_de_empresas` **e**
 * `editar_empresa` na sessão — conveniência: quem recusa é o serviço, e a mensagem dele vai à tela
 * verbatim.
 */

import { Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useEffect, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { LinhaDoRotulo } from '@/shell/MarcaDeObrigatorio'
import { TAMANHO_DO_NOME_DO_GRUPO, chaveDaEmpresa, useCriarGrupo, useEmpresa, useGrupo } from './api'
import { useQueryClient } from '@tanstack/react-query'
import './grupo.css'

const ID_DO_TITULO = 'grupo-da-empresa-titulo'
const ID_DO_NOME = 'grupo-da-empresa-nome'

export const SEM_GRUPO = 'Esta empresa não faz parte de um grupo.'
export const GRUPO_QUE_NAO_ACOMPANHA = 'Esta empresa faz parte de um grupo que você não acompanha.'
export const NOME_LONGO_DEMAIS = `O nome do grupo pode ter até ${String(TAMANHO_DO_NOME_DO_GRUPO)} caracteres.`
const AJUDA =
  'Reúne a matriz e as filiais do cliente — cada uma com o seu CNPJ — para ver os negócios, orçamentos e pedidos de todas numa ficha só.'

/**
 * Os pontos de código do nome como o serviço os conta (`normalizar_o_nome_do_grupo`): NFC, espaços
 * internos colapsados e as pontas aparadas — espaço e caractere de formato (`Cf`).
 */
export function tamanhoDoNome(nome: string): number {
  const normalizado = nome
    .normalize('NFC')
    .replace(/\s+/gu, ' ')
    .replace(/^[\s\p{Cf}]+|[\s\p{Cf}]+$/gu, '')
  return [...normalizado].length
}

function mensagemDe(erro: unknown): string {
  return erro instanceof ErroDaApi ? erro.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

export function GrupoDaEmpresa({ empresaId }: { empresaId: string }) {
  const empresa = useEmpresa(empresaId)
  const grupoId = empresa.data?.grupo_id ?? null
  const grupo = useGrupo(grupoId)
  const sessao = useSessao().data
  const criar = useCriarGrupo()
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const [nome, setNome] = useState('')
  const [erroLocal, setErroLocal] = useState<string | null>(null)
  const relida = useRef<string | null>(null)

  const naoAcompanha =
    grupoId !== null && grupo.error instanceof ErroDaApi && grupo.error.status === 404

  /* Uma releitura da ficha por Grupo ilegível: se ele foi desfeito, o `grupo_id` volta nulo. */
  useEffect(() => {
    if (naoAcompanha && relida.current !== grupoId) {
      relida.current = grupoId
      void clienteDeConsultas.invalidateQueries({ queryKey: chaveDaEmpresa(empresaId) })
    }
  }, [naoAcompanha, grupoId, empresaId, clienteDeConsultas])

  if (empresa.data === undefined) {
    return null
  }

  const permissoes = sessao?.permissoes ?? {}
  //: O Grupo começa por uma Empresa de que se é Responsável (ou pela gestão, D-11): fora disso o
  //: serviço recusa com `409`, e o botão não aparece. Conveniência — quem recusa é o serviço.
  const podeCriarAqui =
    sessao?.escopo_de_carteira === 'todas' || empresa.data.responsavel_id === sessao?.usuario_id
  const podeCriar =
    podeCriarAqui && 'gerir_grupos_de_empresas' in permissoes && 'editar_empresa' in permissoes

  const enviar = (evento: FormEvent) => {
    evento.preventDefault()
    if (tamanhoDoNome(nome) > TAMANHO_DO_NOME_DO_GRUPO) {
      setErroLocal(NOME_LONGO_DEMAIS)
      return
    }
    setErroLocal(null)
    criar.mutate(
      { nome, empresaId },
      {
        onSuccess: (criado) => {
          void navegar({ to: '/grupos/$grupoId', params: { grupoId: criado.id } })
        },
      },
    )
  }

  return (
    <section className="grupo" aria-labelledby={ID_DO_TITULO}>
      <div className="grupo__cartao">
        <div className="grupo__cabecalho">
          <h2 className="grupo__titulo" id={ID_DO_TITULO}>
            Grupo
          </h2>
        </div>
        <div className="grupo__corpo">
          {grupoId !== null ? (
            grupo.data !== undefined ? (
              <p className="grupo__ajuda">
                Esta empresa faz parte do grupo{' '}
                <Link to="/grupos/$grupoId" params={{ grupoId }}>
                  {grupo.data.nome}
                </Link>
                .
              </p>
            ) : naoAcompanha ? (
              <p className="grupo__ajuda">{GRUPO_QUE_NAO_ACOMPANHA}</p>
            ) : grupo.isError ? (
              <p className="grupo__erro" role="alert">
                {mensagemDe(grupo.error)}
              </p>
            ) : (
              <p className="grupo__ajuda" role="status">
                Carregando o grupo…
              </p>
            )
          ) : (
            <>
              <p className="grupo__ajuda">{SEM_GRUPO}</p>
              {podeCriar ? (
                <form className="grupo__formulario" onSubmit={enviar}>
                  {/* 13.15: o `*` fica fora do `<label>` (o nome acessível continua "Nome do
                      grupo"), e por isso o rótulo deixou de embrulhar o campo. */}
                  <div className="grupo__campo">
                    <LinhaDoRotulo obrigatorio>
                      <label htmlFor={ID_DO_NOME}>Nome do grupo</label>
                    </LinhaDoRotulo>
                    <input
                      id={ID_DO_NOME}
                      value={nome}
                      aria-required
                      placeholder="Ex.: Grupo Veluplast"
                      onChange={(evento) => {
                        setNome(evento.target.value)
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="grupo__botao"
                    disabled={nome.trim() === '' || criar.isPending}
                  >
                    Criar grupo
                  </button>
                </form>
              ) : null}
              <p className="grupo__ajuda">{AJUDA}</p>
              {erroLocal !== null || criar.isError ? (
                <p className="grupo__erro" role="alert">
                  {erroLocal ?? mensagemDe(criar.error)}
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  )
}
